import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useSelector } from 'react-redux';
import AppRoutes from 'app/utils/routes.ts';
import { isUserAnyAdminSelector, shouldUseLegacyVadSelector } from 'app/selectors/userSelectors.ts';
import type { ScenarioDto } from '@/api-gen/api.schemas';
import { recordRecentScenario } from './useRecentScenarios.ts';

/**
 * Navigation helpers for opening a scenario, mirroring the routing decisions in
 * ScenarioButton (editor vs player, legacy VAD). Exposes both the resolved paths
 * — so a scenario can be rendered as a real <a> link (openable in a new tab) —
 * and imperative openers. Every open records the scenario as recent. Usable both
 * on the page and in the nav drawer.
 */
export const useScenarioOpen = () => {
  const history = useHistory();
  const isAnyAdmin = useSelector(isUserAnyAdminSelector);
  const useLegacyVad = useSelector(shouldUseLegacyVadSelector);

  const editorPath = useCallback(
    (scene: ScenarioDto) => (scene.id ? generatePath(AppRoutes.SceneWithId, { id: scene.id }) : null),
    []
  );

  const chatPath = useCallback(
    (scene: ScenarioDto) =>
      scene.id ? generatePath(useLegacyVad ? AppRoutes.SceneWithId : AppRoutes.ScenarioPlayer, { id: scene.id }) : null,
    [useLegacyVad]
  );

  // Managers default to the editor; everyone else defaults to a chat/run.
  const defaultPath = useCallback(
    (scene: ScenarioDto) => (isAnyAdmin ? editorPath(scene) : chatPath(scene)),
    [isAnyAdmin, editorPath, chatPath]
  );

  // Record + push. recordRecentScenario also runs from link clicks (see recordOpen)
  // so the "recent" list stays correct however a scenario is opened.
  const navigateTo = useCallback(
    (scene: ScenarioDto, path: string | null, record: boolean) => {
      if (!path || !scene.id) return;
      if (record) recordRecentScenario(scene.id);
      history.push(path);
    },
    [history]
  );

  const openEditor = useCallback(
    (scene: ScenarioDto) => navigateTo(scene, editorPath(scene), true),
    [navigateTo, editorPath]
  );
  const openChat = useCallback(
    (scene: ScenarioDto) => navigateTo(scene, chatPath(scene), true),
    [navigateTo, chatPath]
  );
  // `record` defaults on, but pass false when opening from the recent list itself
  // so the item doesn't jump to the top out from under the click.
  const openDefault = useCallback(
    (scene: ScenarioDto, record = true) => navigateTo(scene, defaultPath(scene), record),
    [navigateTo, defaultPath]
  );

  return { openEditor, openChat, openDefault, defaultPath, recordOpen: recordRecentScenario, isAnyAdmin };
};
